import { getTimeDefinitionService } from '../../../core/services/TimeDefinitionService.js';
import { storageManager } from '../../../core/storage/index.js';
import { eventBus } from '../../../core/events/index.js';
import { GROK_WORKER_URL } from '../../../js/config.js';
import { HangoutService } from '../services/HangoutService.js';
import { IndexedDBService } from '../../../services/IndexedDBService.js';
import { storage, storageMechanism, isPersistableConversationId } from '../../../js/utils/storage.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import {
  extractBackgroundIdFromUrl,
  clearConversationCache as utilsClearCache,
  fetchLocationsMap,
  getCachedLocationsMap,
  locObjectToArray,
  getLocationNameById,
  isValidUserSetLocation
} from '../../../core/utils/hangoutUtils.js';
import {
  getCurrentTimestamps
} from '../../../core/utils/timestampUtils.js';
import { resolvePreferredLocalMessages } from '../shared/history-resolution.js';
import {
  getVisibleMessages,
  resolveActiveVersions,
  resolveCanonicalPath,
  normalizeMessagePath,
  isGreetingMessage,
  getNextPersistableRootPath,
  getActiveBranchTipPath,
  reconcileLocalMessagesWithDbMessages,
  resolveDbPathForLocalMessage,
  isValidLtreePath,
  isDescendantPath,
  isPathOnPendingBranch,
  resolveGreetingChainPath,
  greetingRootSegment,
  getPathRootSegment,
  hasSiblingAssistantRoundParents
} from '../shared/message-tree.js';
import { tokenManager } from '../../../js/utils/TokenManager.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';
import { normalizeRemoteConversationMessages } from '../shared/remote-message-normalize.js';
import {
  extractCharacterData,
  hydrateCharactersFromMessages,
  buildCharacterVisualMap,
  mergeCharacterVisualData
} from '../shared/character-hydrate.js';
import {
  buildConfigCharacterNameMap,
  mapRemoteConversations,
  mapLocalConversation,
  mergeAndSortConversations,
  enrichLocalConversationMetadata
} from '../shared/conversation-list-utils.js';
import {
  HANGOUT_EVENTS,
  STORAGE_KEYS,
  STORAGE_PREFIXES,
  CONFIG_KEY,
  ERROR_VALUES,
  ACTIVE_VERSIONS_STORAGE_KEY
} from '../shared/hangout-constants.js';
import {
  mergeCharacters,
  hasCharacterStateChanged,
  hasIdCollectionChanged
} from '../shared/character-collection-utils.js';
import { reconstructSessionStateFromMessages } from '../shared/session-reconstruct.js';
import { getScenarioDescriptionFromHangoutConfig } from '../shared/scenario-config.js';
import {
  resolveScenarioBackdrop,
  shouldPreserveLocalBackdrop,
  shouldSeedScenarioBackdrop
} from '../shared/session-backdrop.js';

export { HANGOUT_EVENTS };

export class HangoutState {
  static instance;

  static getInstance(options = {}) {
    if (!HangoutState.instance) {
      HangoutState.instance = new HangoutState(options);
    }
    return HangoutState.instance;
  }

  constructor(options = {}) {
    this.storage = options.storage || storageManager;
    this.events = options.events || eventBus;
    this.dbService = new IndexedDBService();

    this.state = {
      currentSession: null,
      messages: [],
      allCharacters: [],
      allScenarios: [],
      allLocations: [],
      conversationId: null,
      isLoading: false,
      error: null,
      currentTime: 'night',
      customScenarioDescription: null,
      customScenarioDescriptionCleared: false,
      customGoals: {},
      recentCharacters: [],
      activeVersions: {},
      pendingBranch: null,
      branchTipPath: null
    };

    this._greetingVariationCache = new Map();

    this.startupPrefetch = null;

    this._setupBackgroundServiceListeners();
    this._ensureHistorySchemaCutover();

    if (typeof window !== 'undefined') {
      window.clearConversationCache = () => {
        utilsClearCache();
        this._emitChange('CONVERSATION_CACHE_CLEARED');
      };
    }

    this._loadState().catch(error => {
      console.error('Failed to load hangout state:', error);
    });

    this._initIndexedDB();
  }

  _ensureHistorySchemaCutover() {
    const targetSchemaVersion = '4';
    const currentVersion = storage.get(STORAGE_KEYS.HISTORY_SCHEMA_VERSION);
    if (currentVersion === targetSchemaVersion) return;
    storage.remove(STORAGE_KEYS.OPENING_GREETING);
    storage.set(STORAGE_KEYS.HISTORY_SCHEMA_VERSION, targetSchemaVersion);
  }

  _getConfig() {
    const config = storageManager.local.getItem(CONFIG_KEY);
    if (!config || typeof config !== 'object') return {};
    return config;
  }

  getHangoutConfig() {
    return this._getConfig();
  }

  _setConfig(config) {
    try {
      const existingConfig = this._getConfig();
      const existingConversationId = typeof existingConfig.conversationId === 'string'
        ? existingConfig.conversationId.trim()
        : '';
      const incomingConversationId = typeof config?.conversationId === 'string'
        ? config.conversationId.trim()
        : '';
      const hasIncomingConversationId = incomingConversationId.length > 0;
      const isExplicitConversationReset = config?.conversationId == null
        && (
          config?.isNewConversation === true
          || config?.forceConversationIdReset === true
        );

      let configToPersist = config;
      if (!hasIncomingConversationId && existingConversationId && !isExplicitConversationReset) {
        configToPersist = {
          ...config,
          conversationId: existingConversationId
        };
      }

      if (Array.isArray(configToPersist?.allCharacters)) {
        configToPersist.allCharacters = configToPersist.allCharacters.map((character) => ({
          ...character,
          actionId: character.actionId ?? null
        }));
      }

      localStorage.setItem(CONFIG_KEY, JSON.stringify(configToPersist));
      storageManager.memory.set(CONFIG_KEY, configToPersist);
      void storageManager.setItem(CONFIG_KEY, configToPersist).catch((error) => {
        console.warn('HangoutState._setConfig: storageManager sync failed:', error);
      });
    } catch (e) {
      console.warn('HangoutState._setConfig: Save failed:', e);
    }
  }

  _updateConfig(updates) {
    const config = this._getConfig();
    Object.assign(config, updates);
    this._setConfig(config);
    return config;
  }

  setStartupPrefetch(prefetch) {
    if (!prefetch || typeof prefetch !== 'object') {
      this.startupPrefetch = null;
      return;
    }
    this.startupPrefetch = prefetch;
  }

  getStartupPrefetch() {
    return this.startupPrefetch;
  }

  consumeStartupPrefetch() {
    const prefetch = this.startupPrefetch;
    this.startupPrefetch = null;
    return prefetch;
  }

  _setupBackgroundServiceListeners() {
    const backgroundService = getBackgroundService();

    backgroundService.subscribe('BACKGROUND_CHANGED', (data) => {
      this._emitChange(HANGOUT_EVENTS.BACKGROUND_CHANGED, data);
    });

    backgroundService.subscribe('LOCATION_CHANGED', (data) => {
      this._emitChange(HANGOUT_EVENTS.BACKGROUND_CHANGED, data);
    });

    backgroundService.subscribe('TIME_CHANGED', (data) => {
      this._emitChange(HANGOUT_EVENTS.TIME_OF_DAY_CHANGED, data);
    });
  }

  async _initIndexedDB() {
    if (this.dbInitPromise) return this.dbInitPromise;

    this.dbInitPromise = (async () => {
      try {
        const { IndexedDBInitializer } = await import('../../../core/utils/indexedDBInitializer.js');
        const dbInitializer = new IndexedDBInitializer();
        const db = await dbInitializer.init();

        if (!db) {
          this.isIndexedDBAvailable = false;
          return null;
        }

        this.dbService.db = db;
        this.dbService.stores = dbInitializer.getStores();
        this.dbService.isInitialized = true;
        this.isIndexedDBAvailable = true;
        return db;
      } catch (error) {
        console.error('IndexedDB init failed:', error);
        this.isIndexedDBAvailable = false;
        return null;
      }
    })();

    return this.dbInitPromise;
  }

  initIndexedDB() {
    return this._initIndexedDB();
  }

  async _loadDataFromIndexedDB(storeName) {
    if (!this.isIndexedDBAvailable) return [];

    this._setLoading(true);
    try {
      const data = await this.dbService.getAllData(this.dbService.stores[storeName]);
      if (data?.length > 0) {
        this.state[`all${storeName.charAt(0).toUpperCase() + storeName.slice(1)}`] = data;
        return data;
      }
      return [];
    } catch (error) {
      console.error(`IndexedDB load failed for ${storeName}:`, error);
      this.setError(error);
      return [];
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    this.state.isLoading = isLoading;
  }

  async _loadLocationsFromCDN() {
    try {
      const lang = storage.get('displayLanguage') || 'en';
      const locationsObject = await fetchLocationsMap(lang);
      const locationsArray = locObjectToArray(locationsObject);
      if (locationsArray.length > 0) {
        this.state.allLocations = locationsArray;
        this._emitChange(HANGOUT_EVENTS.SPOT_SELECTED, locationsArray);
      }
    } catch (error) {
      console.error('CDN locations load failed:', error);
      this._loadCachedLocations();
    }
  }

  _loadCachedLocations() {
    const cachedData = getCachedLocationsMap();
    if (cachedData) {
      this.state.allLocations = locObjectToArray(cachedData);
    }
  }

  async _loadState() {
    try {
      await this._initIndexedDB();

      if (this.isIndexedDBAvailable) {
        await Promise.all([
          this._loadDataFromIndexedDB('characters'),
          this._loadDataFromIndexedDB('scenarios')
        ]);
      }
      await this._loadLocationsFromCDN();

      this.state.messages = this._loadMessagesFromLocalStorage();
      this.state.conversationId = this.getConversationId();
      await this._reconcileIfLocalHasSiblingAssistantRounds(this.state.conversationId);
      const config = this._getConfig();
      this.state.currentTime = config.timeOfDay || 'night';

      this.state.customScenarioDescription = await this.storage.getItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION);

      this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
    } catch (error) {
      console.error('State load failed:', error);
      this.setError(error);
    }
  }

  async _saveState() {
    try {
      if (this.state.customScenarioDescription) {
        await this.storage.setItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION, this.state.customScenarioDescription);
      } else {
        await this.storage.removeItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION);
      }

      await this._saveToIndexedDBIfAvailable();
    } catch (error) {
      console.error('State save failed:', error);
    }
  }

  async _saveToIndexedDBIfAvailable() {
    if (!this.isIndexedDBAvailable) return;

    try {
      const savePromises = [];

      if (this.state.allCharacters.length > 0) {
        const existingCharacters = await this.dbService.getAllData(this.dbService.stores.characters) || [];
        const mergedCharacters = mergeCharacters(existingCharacters, this.state.allCharacters);
        if (hasCharacterStateChanged(existingCharacters, mergedCharacters)) {
          savePromises.push(this._saveToIndexedDB(this.dbService.stores.characters, mergedCharacters));
        }
      }

      const existingScenarios = await this.dbService.getAllData(this.dbService.stores.scenarios) || [];
      if (hasIdCollectionChanged(existingScenarios, this.state.allScenarios)) {
        savePromises.push(this._saveToIndexedDB(this.dbService.stores.scenarios, this.state.allScenarios));
      }

      await Promise.all(savePromises);
    } catch (error) {
      console.error('Failed to save to IndexedDB:', error);
    }
  }

  async _saveToIndexedDB(storeName, data) {
    if (!this.isIndexedDBAvailable || !Array.isArray(data) || data.length === 0) return;

    try {
      if (!this.dbService.db) {
        console.warn('IndexedDB not initialized, skipping save for', storeName);
        return;
      }
      await this.dbService.clearStore(storeName);
      await this.dbService.storeData(storeName, data);
    } catch (error) {
      console.error(`Failed to save to IndexedDB store ${storeName}:`, error);
    }
  }

  _emitChange(eventType, data = null) {
    if (this._isEmitting) {
      console.warn('Prevented recursive event emission for:', eventType);
      return;
    }
    this._isEmitting = true;
    try {
      this.events.emit(eventType, data);
      if (eventType !== HANGOUT_EVENTS.STATE_CHANGED) {
        this.events.emit(HANGOUT_EVENTS.STATE_CHANGED, { type: eventType, data });
      }
    } finally {
      this._isEmitting = false;
    }
  }

  setError(error) {
    this.state.error = error;
  }

  async setCharacters(characters) {
    this.state.allCharacters = characters;
    await this._saveToIndexedDB(this.dbService.stores.characters, characters);
  }

  async setScenarios(scenarios) {
    this.state.allScenarios = scenarios;
    await this._saveToIndexedDB(this.dbService.stores.scenarios, scenarios);
  }

  async updateScenario(scenario) {
    if (!scenario || !scenario.id) return;
    const idx = this.state.allScenarios.findIndex(s => String(s.id) === String(scenario.id));
    if (idx !== -1) {
      this.state.allScenarios[idx] = scenario;
    } else {
      this.state.allScenarios.push(scenario);
    }
    await this._saveToIndexedDB(this.dbService.stores.scenarios, this.state.allScenarios);
  }

  async setSelectedCharacter(character) {
    if (!character) return;

    const existingChar = this.state.allCharacters.find(c => c.id === character.id);
    if (existingChar) {
      Object.assign(existingChar, {
        name: character.name || existingChar.name
      });
    } else {
      await this.addCharacter(character);
    }

    const config = this._getConfig();
    if (config.allCharacters) {
      const activeCharacterIds = config.allCharacters.map(c => String(c.id));
      if (!activeCharacterIds.includes(String(character.id))) {
        this._updateConfig({
          activeCharacterIds: [...activeCharacterIds, String(character.id)]
        });
      }
    }

    this._updateRecentCharacters(character);
    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }

  _updateRecentCharacters(character) {
    this.state.recentCharacters = this.state.recentCharacters || [];
    const existingIndex = this.state.recentCharacters.findIndex(c => c.id === character.id);

    if (existingIndex !== -1) {
      this.state.recentCharacters.splice(existingIndex, 1);
    }

    this.state.recentCharacters.unshift({
      id: character.id,
      name: character.name,
      avatar: character.avatar,
      lastInteraction: new Date().toISOString()
    });

    if (this.state.recentCharacters.length > 10) {
      this.state.recentCharacters = this.state.recentCharacters.slice(0, 10);
    }
  }

  async setSelectedScenario(scenario) {
    if (!scenario) return;

    const existingScenario = this.state.allScenarios.find(s => s.id === scenario.id);
    if (!existingScenario) {
      this.state.allScenarios.push(scenario);
    }

    await this.switchScenario(scenario);

    const updates = {
      scenarioId: scenario.id
    };

    const currentConfig = this._getConfig();
    const preserveLocalBackdrop = shouldPreserveLocalBackdrop(currentConfig);
    if (shouldSeedScenarioBackdrop(currentConfig)) {
      const resolved = resolveScenarioBackdrop(scenario);
      if (resolved?.locId) updates.loc_id = resolved.locId;
      if (resolved?.timeOfDay) updates.timeOfDay = resolved.timeOfDay;
      if (resolved?.timeHour != null) updates.timeHour = resolved.timeHour;
      const locForUrl = updates.loc_id || currentConfig.loc_id;
      const timeForUrl = updates.timeOfDay || currentConfig.timeOfDay;
      if (locForUrl && timeForUrl) {
        updates.backgroundUrl = getBackgroundService().constructBackgroundUrl(locForUrl, timeForUrl);
      }
    } else if (!preserveLocalBackdrop) {
      const currentLocId = currentConfig.loc_id;
      const backgroundLocationId = scenario.loc ?? scenario.loc_id;
      if (backgroundLocationId !== undefined && backgroundLocationId !== null && !isValidUserSetLocation(currentLocId)) {
        updates.loc_id = String(backgroundLocationId);
        const existingTimeOfDay = currentConfig.timeOfDay;
        const validTimeOfDayValues = getTimeDefinitionService().getValidTimePeriods();
        const hasValidExistingTimeOfDay = existingTimeOfDay && validTimeOfDayValues.includes(existingTimeOfDay);
        const scenarioTime = scenario.time || 3;
        const timeOfDay = hasValidExistingTimeOfDay
          ? existingTimeOfDay
          : getTimeDefinitionService().getTimeOfDayFromNumericSlot(scenarioTime);
        if (!hasValidExistingTimeOfDay) {
          updates.timeOfDay = timeOfDay;
        }
        updates.backgroundUrl = getBackgroundService().constructBackgroundUrl(backgroundLocationId, timeOfDay);
      }
    }

    this._updateConfig(updates);
    if (!preserveLocalBackdrop) {
      await getBackgroundService().syncWithScenario();
    }

    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }

  async setSelectedOutfit(outfitId) {
    outfitId = String(outfitId);

    this._updateConfig({ outfitId });

    const activeCharacters = this.state.allCharacters.filter(c => c.isActive);
    if (activeCharacters.length > 0) {
      activeCharacters.forEach(char => char.outfitId = outfitId);
    }
    this._updateCharacterInConfig(activeCharacters[0]?.id, { outfitId });

    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.OUTFIT_SELECTED, { outfitId, characters: activeCharacters });
  }

  getSelectedOutfit() {
    const config = this._getConfig();
    const primaryCharacter = config.allCharacters?.[0];
    return primaryCharacter?.outfitId ||
      this.state.allCharacters.find(c => c.isActive)?.outfitId ||
      ERROR_VALUES.MISSING_CHARACTER;
  }

  async setSelectedBackground(backgroundUrl) {
    if (typeof backgroundUrl === 'object' && backgroundUrl !== null) {
      backgroundUrl = backgroundUrl.backgroundUrl || backgroundUrl.url || String(backgroundUrl);
    }

    let finalBackgroundUrl = backgroundUrl;
    let loc_id = '0';

    const config = this._getConfig();
    const timeOfDay = config.timeOfDay || 'night';

    const numericId = Number(backgroundUrl);
    if (!isNaN(numericId)) {
      loc_id = numericId.toString();
      finalBackgroundUrl = getBackgroundService().constructBackgroundUrl(numericId, timeOfDay);
    } else if (typeof backgroundUrl === 'string' && !backgroundUrl.startsWith('http')) {
      let locations = this.state.allLocations;
      if (!locations || locations.length === 0) {
        await this._loadLocationsFromCDN();
        locations = this.state.allLocations;
      }

      const location = locations.find(loc =>
        loc.name && loc.name.toLowerCase() === backgroundUrl.toLowerCase()
      );

      if (location) {
        loc_id = location.id.toString();
        finalBackgroundUrl = getBackgroundService().constructBackgroundUrl(loc_id, timeOfDay);
      } else {
        loc_id = extractBackgroundIdFromUrl(backgroundUrl);
      }
    } else {
      loc_id = extractBackgroundIdFromUrl(backgroundUrl);
    }

    this._updateConfig({
      backgroundUrl: finalBackgroundUrl,
      loc_id: loc_id
    });

    await this._saveState();

    const conversationId = this.getConversationId();
    if (conversationId && finalBackgroundUrl) {
      await this._saveMessagesToLocalStorage();
    }

    this._emitChange(HANGOUT_EVENTS.BACKGROUND_CHANGED, finalBackgroundUrl);
  }

  async getSelectedBackground() {
    try {
      const currentBackground = await getBackgroundService().getCurrentBackground();
      return currentBackground.backgroundUrl;
    } catch (error) {
      console.error('getSelectedBackground failed:', error);
      return ERROR_VALUES.MISSING_BACKGROUND;
    }
  }

  async startSession(session) {
    if (this.state.conversationId && session.conversationId && this.state.conversationId === session.conversationId) return;

    const existingConversationId = this.getConversationId();
    const normalizedIncomingConversationId = typeof session?.conversationId === 'string'
      ? session.conversationId.trim()
      : null;
    const effectiveConversationId = normalizedIncomingConversationId || existingConversationId || null;

    this.state.conversationId = effectiveConversationId;

    const hangoutConfig = this._getConfig();
    const preserveLocalBackdrop = shouldPreserveLocalBackdrop(hangoutConfig);
    hangoutConfig.conversationId = this.state.conversationId;
    hangoutConfig.isNewConversation = !this.state.conversationId;
    this._setConfig(hangoutConfig);

    if (!preserveLocalBackdrop) {
      storage.remove('hangout_manual_time_set');
    }

    this._updateConfig({
      conversationId: effectiveConversationId,
      scenarioId: session.scenarioId,
      backgroundUrl: session.backgroundUrl || (preserveLocalBackdrop ? hangoutConfig.backgroundUrl || null : null),
      loc_id: session.loc_id || hangoutConfig.loc_id || null,
      type: 'scenario',
      isNewConversation: !effectiveConversationId
    });

    const currentConfig = this._getConfig();
    const startupPrefetch = session?.startupPrefetch || this.getStartupPrefetch();
    const scenarioPrefetchPromise = startupPrefetch?.scenarioPromise;
    if (!preserveLocalBackdrop && !currentConfig.backgroundUrl && session.scenarioId && scenarioPrefetchPromise) {
      const hangoutService = HangoutService.getInstance();
      void scenarioPrefetchPromise.then((scenario) => {
        const latestConfig = this._getConfig();
        if (latestConfig.backgroundUrl) return;
        const backgroundLocationId = scenario?.loc_id ?? scenario?.loc ?? null;
        if (!backgroundLocationId) return;
        this._updateConfig({
          loc_id: String(backgroundLocationId),
          backgroundUrl: hangoutService.getBackgroundUrl(backgroundLocationId)
        });
      }).catch((error) => {
        console.warn('HangoutState: Prefetched scenario background update failed:', error);
      });
    }

    if (session.characterId && session.scenarioId) {
      const selectedScenario = this.getSelectedScenario();
      if (selectedScenario?.goal && !this.state.customGoals?.[session.characterId]) {
        this.state.customGoals = this.state.customGoals || {};
        const hangoutConfig = this._getConfig();
        hangoutConfig.goal = selectedScenario.goal;
        this._setConfig(hangoutConfig);
      }
    }

    const existingMessages = this._loadMessagesFromLocalStorage();
    this.state.messages = existingMessages.length > 0 ? existingMessages : [];

    if (session.characterId) {
      await this.addCharacter({ id: session.characterId });
      const character = this.state.allCharacters.find(c => String(c.id) === String(session.characterId));
      if (character) {
        character.isActive = true;
      }
      this._updateConfig({
        activeCharacterIds: [String(session.characterId)]
      });
    }

    await this._saveState();

    this._emitChange(HANGOUT_EVENTS.SESSION_STARTED, session);
  }

  async _fetchRemoteConversationMessages(conversationId) {
    const isPrivateScenario = this._getConfig()?.isPrivateScenario === true;
    const messagesApiUrl = `${GROK_WORKER_URL}/v1/hangout/conversation?conversationId=${conversationId}&language=${encodeURIComponent(storage.getLanguage?.() || 'en')}&isPrivateScenario=${isPrivateScenario ? 'true' : 'false'}`;
    try {
      const data = await fetchCache.fetchJSON(messagesApiUrl, {
        headers: {
          Authorization: `Bearer ${await tokenManager.getValidAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });
      const rawMessages = Array.isArray(data.messages)
        ? data.messages
        : (Array.isArray(data.history) ? data.history : []);
      this.setGreetingVariationMeta({
        variation: data.greetingVariation,
        count: data.greetingVariationCount
      });
      const messages = normalizeRemoteConversationMessages(rawMessages, this._getConfig()?.allCharacters);
      this._hydrateCharacterActionNamesFromMessages(messages);
      return { messages, data, ok: true };
    } catch (error) {
      console.error('HangoutState: Message fetch failed:', conversationId, error);
      return { messages: [], data: null, ok: false };
    }
  }

  _hydrateCharacterActionNamesFromMessages(messages = []) {
    const config = this._getConfig();
    const allCharacters = Array.isArray(config.allCharacters) ? config.allCharacters : [];
    const { characters, didChange } = hydrateCharactersFromMessages(allCharacters, messages, config);
    if (!didChange) return;
    config.allCharacters = characters;
    this._setConfig(config);
  }

  _normalizeGreetingVariationMeta({ variation = 0, count = 1 } = {}) {
    const parsedVariation = Number.parseInt(String(variation ?? 0), 10);
    const parsedCount = Number.parseInt(String(count ?? 1), 10);
    return {
      variation: Number.isFinite(parsedVariation) && parsedVariation >= 0 ? parsedVariation : 0,
      count: Number.isFinite(parsedCount) && parsedCount > 0 ? parsedCount : 1
    };
  }

  setGreetingVariationMeta({ variation = 0, count = 1 } = {}) {
    const meta = this._normalizeGreetingVariationMeta({ variation, count });
    const current = this.getGreetingVariationMeta();
    if (current.variation === meta.variation && current.count === meta.count) {
      return;
    }
    this._updateConfig({
      greetingVariation: meta.variation,
      greetingVariationCount: meta.count
    });
  }

  getGreetingVariationMeta() {
    const config = this._getConfig();
    return this._normalizeGreetingVariationMeta({
      variation: config.greetingVariation,
      count: config.greetingVariationCount
    });
  }

  applyGreetingVariationMessages(greetingMessages = []) {
    const incoming = Array.isArray(greetingMessages) ? greetingMessages : [];
    const current = Array.isArray(this.state.messages) ? this.state.messages : [];
    const roster = this._getConfig()?.allCharacters || [];
    const variation = this.getGreetingVariationMeta().variation;
    const normalizedGreetings = normalizeRemoteConversationMessages(incoming, roster)
      .map((message) => ({
        ...message,
        path: resolveGreetingChainPath(message.characterId ?? message.character_id, roster, variation),
        isGreeting: true,
        isInitialGreeting: true
      }));
    const incomingPaths = new Set(
      normalizedGreetings.map((message) => normalizeMessagePath(message.path)).filter(Boolean)
    );
    const kept = current.filter((message) => {
      const path = normalizeMessagePath(message?.path);
      if (incomingPaths.has(path) && isGreetingMessage(message)) return false;
      return true;
    });
    this.setMessages([...kept, ...normalizedGreetings]);
    this.syncBranchTipToSelectedGreetingRoot();
  }

  _cloneGreetingMessages(messages = [], variation = 0) {
    const root = greetingRootSegment(variation);
    return (Array.isArray(messages) ? messages : [])
      .filter((message) => {
        if (!(message?.isGreeting === true || message?.isInitialGreeting === true || isGreetingMessage(message))) {
          return false;
        }
        return getPathRootSegment(message?.path) === root;
      })
      .map((message) => ({ ...message }));
  }

  rememberGreetingVariation(variation, messages) {
    const parsedVariation = Number.parseInt(String(variation ?? 0), 10);
    if (!Number.isFinite(parsedVariation) || parsedVariation < 0) return;
    const cloned = this._cloneGreetingMessages(messages ?? this.getMessages(), parsedVariation);
    if (cloned.length === 0) return;
    if (!(this._greetingVariationCache instanceof Map)) {
      this._greetingVariationCache = new Map();
    }
    this._greetingVariationCache.set(parsedVariation, cloned);
  }

  getCachedGreetingVariation(variation) {
    const parsedVariation = Number.parseInt(String(variation ?? 0), 10);
    if (!Number.isFinite(parsedVariation) || parsedVariation < 0) return null;
    const cached = this._greetingVariationCache instanceof Map
      ? this._greetingVariationCache.get(parsedVariation)
      : null;
    if (!Array.isArray(cached) || cached.length === 0) return null;
    return cached.map((message) => ({ ...message }));
  }

  clearGreetingVariationCache() {
    this._greetingVariationCache = new Map();
  }

  async reconcileTranscriptWithRemote(conversationId = null) {
    const resolvedConversationId = conversationId || this.getConversationId();
    if (!resolvedConversationId) {
      return { success: false, error: 'Missing conversationId', messages: this.getMessages() };
    }
    const remote = await this._fetchRemoteConversationMessages(resolvedConversationId);
    if (!remote.ok) {
      return { success: false, error: 'Remote fetch failed', messages: this.getMessages() };
    }
    const localMessages = Array.isArray(this.state.messages) && this.state.messages.length > 0
      ? this.state.messages
      : (resolvePreferredLocalMessages(resolvedConversationId).messages || []);
    const reconciled = reconcileLocalMessagesWithDbMessages(localMessages, remote.messages);

    // Apply in-memory immediately; do not use setMessages (unawaited persist race).
    const reconciledList = Array.isArray(reconciled) ? reconciled : [];
    this.state.messages = reconciledList;
    this.state.activeVersions = resolveActiveVersions(this.state.messages, this.getActiveVersions());
    this._persistActiveVersions();
    this._loadActiveVersionsForConversation(resolvedConversationId);

    try {
      const persisted = storage.replaceConversationMessagesAtomic(
        resolvedConversationId,
        reconciledList,
        {
          metadata: { scenarioId: remote.data?.scenarioId || null },
          reason: 'reconcile-transcript-with-remote',
          enforceAllOrNothing: false
        }
      );
      if (!persisted) {
        storage.set(STORAGE_KEYS.HISTORY, reconciledList);
        console.warn(
          'HangoutState.reconcileTranscriptWithRemote: atomic persist blocked; forced hangout:history override'
        );
      }
    } catch (persistError) {
      storage.set(STORAGE_KEYS.HISTORY, reconciledList);
      console.warn('HangoutState.reconcileTranscriptWithRemote: persist failed', persistError);
    }

    return { success: true, messages: this.state.messages, remoteMessages: remote.messages };
  }

  async _reconcileIfLocalHasSiblingAssistantRounds(conversationId = null) {
    const resolvedConversationId = conversationId || this.getConversationId();
    if (!resolvedConversationId) return false;
    if (!hasSiblingAssistantRoundParents(this.state.messages)) return false;
    const result = await this.reconcileTranscriptWithRemote(resolvedConversationId);
    return result?.success === true;
  }

  async _fetchExistingMessages(conversationId) {
    const { messages: localMessages } = resolvePreferredLocalMessages(conversationId);
    const remote = await this._fetchRemoteConversationMessages(conversationId);
    const remoteMessages = remote.ok ? remote.messages : [];
    const data = remote.data || {};

    let deduped = [];
    if (remoteMessages.length > 0 && Array.isArray(localMessages) && localMessages.length > 0) {
      deduped = reconcileLocalMessagesWithDbMessages(localMessages, remoteMessages);
    } else if (remoteMessages.length > 0) {
      deduped = remoteMessages;
    } else if (Array.isArray(localMessages) && localMessages.length > 0) {
      deduped = localMessages;
    }

    if (deduped.length === 0) return;

    this.setMessages(deduped);
    this._loadActiveVersionsForConversation(conversationId);
    storage.setConversationMessages(conversationId, deduped, {
      scenarioId: data.scenarioId || null
    });

    const currentBackground = await this.getSelectedBackground();
    if (currentBackground) {
      const backgroundId = extractBackgroundIdFromUrl(currentBackground);
      if (backgroundId) {
        await getBackgroundService().setBackground(backgroundId, this.getCurrentTimeSlot() || 'night');
      }
    }

    const lastCharacterMessage = deduped.slice().reverse().find(msg => msg.role === 'assistant');
    if (lastCharacterMessage) {
      this._emitChange(HANGOUT_EVENTS.CONVERSATION_RESUMED, {
        lastMessage: lastCharacterMessage,
        totalMessages: deduped.length,
        shouldUpdateUI: true,
        conversationId,
        isPageRefresh: true,
        allMessages: deduped,
        stateReconstructed: true
      });
    }

    return deduped;
  }


  async addMessage(message, isOutgoing = false) {
    if (!message) {
      console.warn('HangoutState.addMessage: No message provided');
      return { success: false, error: 'No message provided' };
    }

    let messageContent = '';
    if (typeof message === 'string') {
      messageContent = message;
      message = { content: messageContent, role: isOutgoing ? 'user' : 'assistant' };
    } else if (typeof message === 'object') {
      messageContent = message.content || message.text || message.message || '';
      if (!message.role) {
        message.role = isOutgoing ? 'user' : 'assistant';
      }
    }

    messageContent = String(messageContent || '');

    const allowEmptyAssistant = message.role === 'assistant' && Boolean(String(message.path || '').trim());
    if ((!messageContent || messageContent.trim() === '') && !allowEmptyAssistant) {
      console.warn('HangoutState.addMessage: Empty message content');
      return { success: false, error: 'Empty message content' };
    }

    const timestamps = getCurrentTimestamps();
    const createdAt = message.createdAt || message.timestamp || timestamps.js;
    const currentMessages = Array.isArray(this.state.messages) ? this.state.messages : [];
    const isGreeting = message.isGreeting === true
      || message.isInitialGreeting === true
      || isGreetingMessage(message);
    let nextPath = message.path ? String(message.path) : '';
    if (isGreeting) {
      if (!isValidLtreePath(nextPath)) {
        const roster = this.state.allCharacters || this._getConfig()?.allCharacters || [];
        nextPath = resolveGreetingChainPath(
          message.characterId,
          roster,
          this.getGreetingVariationMeta().variation
        );
      }
    } else if (!nextPath) {
      nextPath = getNextPersistableRootPath(
        currentMessages,
        this.getActiveVersions(),
        this.getBranchFilterOptions()
      );
    } else if (!isValidLtreePath(nextPath) && !String(nextPath).startsWith('outfit_change_')) {
      nextPath = getNextPersistableRootPath(
        currentMessages,
        this.getActiveVersions(),
        this.getBranchFilterOptions()
      );
    }

    const enhancedMessage = {
      ...message,
      path: String(nextPath),
      content: messageContent.trim(),
      createdAt,
      role: message.role,
      conversationId: this.getConversationId(),
      ...(isGreeting ? { isGreeting: true, isInitialGreeting: true } : {})
    };

    this.state.messages = [...this.state.messages, enhancedMessage];

    const shouldAdoptBranch = this.shouldAdoptIncomingPath(enhancedMessage.path);

    if (
      shouldAdoptBranch
      && enhancedMessage.role === 'assistant'
      && enhancedMessage.path
      && !enhancedMessage.isGreeting
    ) {
      const canonicalPath = resolveCanonicalPath(enhancedMessage.path, this.state.messages);
      this.ensureActiveVersion(canonicalPath, enhancedMessage.path);
    }

    if (shouldAdoptBranch && !isGreeting && isValidLtreePath(String(enhancedMessage.path))) {
      this.setBranchTipPath(enhancedMessage.path);
    }

    await this._saveMessagesToLocalStorage();
    await this._saveState();

    this._emitChange(isOutgoing ? HANGOUT_EVENTS.MESSAGE_SENT : HANGOUT_EVENTS.MESSAGE_RECEIVED, {
      message: enhancedMessage,
      messagePath: enhancedMessage.path
    });

    return {
      success: true,
      messagePath: enhancedMessage.path
    };
  }

  setMessages(messages) {
    const incoming = Array.isArray(messages) ? messages : [];
    this.state.messages = incoming;
    const inMemory = this.getActiveVersions();
    const hasInMemory = Object.keys(inMemory).length > 0;
    const storedBranch = this._readStoredBranchState(this.getConversationId());
    const preferred = hasInMemory ? inMemory : storedBranch.activeVersions;
    this.state.activeVersions = resolveActiveVersions(this.state.messages, preferred);
    if (!hasInMemory) {
      this.state.branchTipPath = normalizeMessagePath(storedBranch.branchTipPath);
    }
    this._persistActiveVersions();
    this._saveMessagesToLocalStorage().catch(error => {
      console.error('HangoutState: Message save failed:', error);
    });
  }

  async _reconstructSessionStateFromMessages(messages) {
    const hangoutConfig = await storageManager.getItem(CONFIG_KEY) || {};
    return reconstructSessionStateFromMessages(messages, {
      hangoutConfig,
      fetchScenarioDetails: (scenarioId) => HangoutService.getInstance().getScenarioDetails(scenarioId)
    });
  }

  async _saveMessagesToLocalStorage() {
    const conversationId = this.getConversationId();
    if (!Array.isArray(this.state.messages)) return;

    const historyWritePolicy = this._resolveHistoryWritePolicy();
    const persisted = storage.replaceConversationMessagesAtomic(conversationId || null, this.state.messages, {
      reason: historyWritePolicy.reason,
      enforceAllOrNothing: historyWritePolicy.enforceAllOrNothing
    });
    if (!persisted) {
      console.warn('[Hangout][History] Transcript write skipped by all-or-nothing guard', {
        reason: historyWritePolicy.reason,
        entrypoint: historyWritePolicy.entrypoint,
        messageCount: this.state.messages.length
      });
      return;
    }

    const normalizedHistory = storage.getConversationMessages(conversationId || null);
    if (Array.isArray(normalizedHistory)) {
      this.state.messages = normalizedHistory;
    }
    if (this.state.messages.length === 0) return;
  }

  _resolveHistoryWritePolicy() {
    const config = this._getConfig();
    const entrypoint = typeof config?.entrypoint === 'string'
      ? config.entrypoint.trim()
      : '';
    const enforceAllOrNothing = entrypoint === 'hangout_page_refresh';
    return {
      entrypoint,
      enforceAllOrNothing,
      reason: enforceAllOrNothing ? 'refresh-resume-write' : 'default-write'
    };
  }

  _updateCharacterInConfig(characterId, updates) {
    const config = this._getConfig();
    if (config.allCharacters) {
      const charIndex = config.allCharacters.findIndex(c => String(c.id) === String(characterId));
      if (charIndex !== -1) {
        Object.assign(config.allCharacters[charIndex], updates);
      }
    }

    this._setConfig(config);
    return config;
  }

  updateCharacterInConfig(characterId, updates) {
    return this._updateCharacterInConfig(characterId, updates);
  }

  _loadMessagesFromLocalStorage() {
    const persistedMessages = storage.get(STORAGE_KEYS.HISTORY);
    return Array.isArray(persistedMessages) ? persistedMessages : [];
  }

  _getLocalConversationIds() {
    return storage.getAllConversationIds() || [];
  }

  _getLocalConversationMetadata(conversationId) {
    const metadata = storage.getConversationMetadata(conversationId);
    if (!metadata) return null;
    const messages = storage.getConversationMessages(conversationId) || [];
    return enrichLocalConversationMetadata(conversationId, metadata, messages);
  }

  getCurrentSession() {
    const config = this._getConfig();
    if (!config.allCharacters?.length) return null;

    const primaryCharacterId = config.allCharacters[0]?.id;
    const session = {
      conversationId: config.conversationId,
      characterId: primaryCharacterId,
      scenarioId: config.scenarioId,
      loc_id: config.loc_id || ERROR_VALUES.MISSING_LOCATION,
      backgroundUrl: typeof config.backgroundUrl === 'object' && config.backgroundUrl !== null
        ? config.backgroundUrl.toString()
        : config.backgroundUrl,
      outfitId: config.allCharacters?.[0]?.outfitId || ERROR_VALUES.MISSING_CHARACTER,
      timeOfDay: config.timeOfDay || ERROR_VALUES.MISSING_TIME,
      isNewConversation: config.isNewConversation || false,
      allCharacters: config.allCharacters || null,
      isMultiCharacter: config.allCharacters?.length > 1,
      startTime: config.startTime || Date.now()
    };

    return session;
  }

  getMessages() {
    return this.state.messages;
  }

  getActiveVersions() {
    return { ...(this.state.activeVersions || {}) };
  }

  getBranchTipPath() {
    return this.state.branchTipPath || null;
  }

  shouldAdoptIncomingPath(path) {
    const normalized = normalizeMessagePath(path);
    if (!normalized || !isValidLtreePath(normalized)) return true;
    if (isPathOnPendingBranch(normalized, this.state.pendingBranch)) return true;
    const tip = this.getBranchTipPath();
    if (!tip) return true;
    return isDescendantPath(normalized, tip);
  }

  getBranchFilterOptions() {
    return {
      pendingBranch: this.state.pendingBranch || null,
      branchTipPath: this.getBranchTipPath(),
      greetingVariation: this.getGreetingVariationMeta().variation
    };
  }

  syncBranchTipToSelectedGreetingRoot(variation = this.getGreetingVariationMeta().variation) {
    const root = greetingRootSegment(variation);
    const onRoot = (Array.isArray(this.state.messages) ? this.state.messages : []).filter((message) => {
      const path = normalizeMessagePath(message?.path);
      return path && isValidLtreePath(path) && getPathRootSegment(path) === root;
    });
    const tip = getActiveBranchTipPath(onRoot);
    if (tip) this.setBranchTipPath(tip);
  }

  getVisibleMessages() {
    return getVisibleMessages(
      this.state.messages || [],
      this.getActiveVersions(),
      this.getBranchFilterOptions()
    );
  }

  setBranchTipPath(path) {
    const normalized = normalizeMessagePath(path);
    const nextPath = normalized && isValidLtreePath(normalized) ? normalized : null;
    if (this.state.branchTipPath === nextPath) return false;
    this.state.branchTipPath = nextPath;
    this._persistActiveVersions();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      branchTipPath: nextPath
    });
    return true;
  }

  syncBranchTipFromVisible() {
    const tipPath = getActiveBranchTipPath(this.getVisibleMessages());
    if (!tipPath) return false;
    return this.setBranchTipPath(tipPath);
  }

  beginPendingBranch(canonicalPath, selectedPath, options = {}) {
    const normalizedCanonical = normalizeMessagePath(canonicalPath);
    const normalizedSelected = normalizeMessagePath(selectedPath);
    if (!normalizedCanonical || !normalizedSelected) return false;
    const hiddenPaths = Array.isArray(options?.hiddenPaths)
      ? [...new Set(options.hiddenPaths.map((path) => normalizeMessagePath(path)).filter(Boolean))]
      : [];
    this.state.pendingBranch = {
      canonicalPath: normalizedCanonical,
      selectedPath: normalizedSelected,
      ...(hiddenPaths.length > 0 ? { hiddenPaths } : {})
    };
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      pendingBranch: this.state.pendingBranch
    });
    return true;
  }

  clearPendingBranch() {
    if (!this.state.pendingBranch) return false;
    this.state.pendingBranch = null;
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      pendingBranch: null
    });
    return true;
  }

  _persistActiveVersions() {
    const conversationId = this.getConversationId();
    storage.set(ACTIVE_VERSIONS_STORAGE_KEY, {
      conversationId: conversationId || null,
      activeVersions: this.getActiveVersions(),
      branchTipPath: this.getBranchTipPath()
    });
  }

  _readStoredBranchState(conversationId = null) {
    const empty = { activeVersions: {}, branchTipPath: null };
    const stored = storage.get(ACTIVE_VERSIONS_STORAGE_KEY);
    if (!stored || typeof stored !== 'object') return empty;
    const storedConversationId = stored.conversationId ? String(stored.conversationId) : null;
    const normalizedConversationId = conversationId ? String(conversationId) : null;
    if (
      normalizedConversationId
      && storedConversationId
      && storedConversationId !== normalizedConversationId
    ) {
      return empty;
    }
    const activeVersions = stored.activeVersions && typeof stored.activeVersions === 'object'
      ? stored.activeVersions
      : {};
    const branchTipPath = normalizeMessagePath(stored.branchTipPath);
    return {
      activeVersions,
      branchTipPath: branchTipPath && isValidLtreePath(branchTipPath) ? branchTipPath : null
    };
  }


  _loadActiveVersionsForConversation(conversationId = null) {
    const storedBranch = this._readStoredBranchState(conversationId);
    this.state.activeVersions = resolveActiveVersions(this.state.messages || [], storedBranch.activeVersions);
    this.state.branchTipPath = storedBranch.branchTipPath;
  }

  loadActiveVersionsForConversation(conversationId = null) {
    return this._loadActiveVersionsForConversation(conversationId);
  }

  setActiveVersion(canonicalPath, selectedPath) {
    const normalizedCanonical = normalizeMessagePath(canonicalPath);
    const normalizedSelected = normalizeMessagePath(selectedPath);
    if (!normalizedCanonical || !normalizedSelected) return false;
    this.state.activeVersions = {
      ...this.getActiveVersions(),
      [normalizedCanonical]: normalizedSelected
    };
    this._persistActiveVersions();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      activeVersions: this.getActiveVersions(),
      canonicalPath: normalizedCanonical,
      selectedPath: normalizedSelected
    });
    return true;
  }

  applyActiveVersions(activeVersions = {}) {
    if (!activeVersions || typeof activeVersions !== 'object') return false;
    const next = { ...this.getActiveVersions() };
    let changed = false;
    for (const [canonicalPath, selectedPath] of Object.entries(activeVersions)) {
      const normalizedCanonical = normalizeMessagePath(canonicalPath);
      const normalizedSelected = normalizeMessagePath(selectedPath);
      if (!normalizedCanonical || !normalizedSelected) continue;
      if (next[normalizedCanonical] === normalizedSelected) continue;
      next[normalizedCanonical] = normalizedSelected;
      changed = true;
    }
    if (!changed) return false;
    this.state.activeVersions = next;
    this._persistActiveVersions();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      activeVersions: this.getActiveVersions()
    });
    return true;
  }

  ensureActiveVersion(canonicalPath, selectedPath) {
    const normalizedCanonical = normalizeMessagePath(canonicalPath);
    const normalizedSelected = normalizeMessagePath(selectedPath);
    if (!normalizedCanonical || !normalizedSelected) return;
    const current = normalizeMessagePath(this.state.activeVersions?.[normalizedCanonical]);
    if (current === normalizedSelected) return;
    this.setActiveVersion(normalizedCanonical, normalizedSelected);
  }

  async _postMessageEdit(conversationId, path, message) {
    const token = await tokenManager.getValidAccessToken();
    if (!token) return { ok: false, error: 'Authentication required', status: 401 };
    const workerUrl = `${GROK_WORKER_URL.replace(/\/+$/, '')}/v1/hangout/message/edit`;
    try {
      await fetchCache.fetchJSON(workerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ conversationId, path, message })
      });
      return { ok: true, status: 200, error: null, code: null };
    } catch (error) {
      return {
        ok: false,
        status: error.status || 500,
        error: error.body?.error || error.message || 'Remote edit failed',
        code: error.body?.code || null
      };
    }
  }

  async updateMessageContent(messagePath, newContent, options = {}) {
    const normalizedPath = normalizeMessagePath(messagePath);
    if (!normalizedPath) return { success: false, error: 'Missing message path' };

    const nextContent = String(newContent || '').trim();
    if (!nextContent) return { success: false, error: 'Empty message content' };

    const messages = Array.isArray(this.state.messages) ? [...this.state.messages] : [];
    const messageIndex = messages.findIndex((message) => normalizeMessagePath(message?.path) === normalizedPath);
    if (messageIndex < 0) return { success: false, error: 'Message not found' };

    const existing = messages[messageIndex];
    const nextMessage = {
      ...existing,
      content: nextContent,
      ...(options.extraUpdates && typeof options.extraUpdates === 'object' ? options.extraUpdates : {})
    };
    messages[messageIndex] = nextMessage;
    this.state.messages = messages;
    await this._saveMessagesToLocalStorage();

    const conversationId = this.getConversationId();
    const isGreeting = isGreetingMessage(existing) || isGreetingMessage(nextMessage);
    const shouldPersistRemote = !isGreeting && options.persistRemote !== false && Boolean(conversationId);
    let persistedPath = normalizeMessagePath(nextMessage.path) || normalizedPath;
    if (shouldPersistRemote) {
        let remoteResult = await this._postMessageEdit(conversationId, persistedPath, nextContent);
        const shouldReconcile = !remoteResult.ok && (
          remoteResult.error === 'Message not found'
          || remoteResult.code === 'message_edit_failed'
          || !isValidLtreePath(persistedPath)
        );
        if (shouldReconcile) {
          const preReconcileMessages = Array.isArray(messages) ? [...messages] : [];
          const reconcileResult = await this.reconcileTranscriptWithRemote(conversationId);
          if (reconcileResult.success) {
            const remappedPath = resolveDbPathForLocalMessage({
              localMessages: preReconcileMessages,
              dbMessages: reconcileResult.remoteMessages || [],
              localPath: normalizedPath,
              characterId: existing.characterId,
              role: existing.role,
              content: existing.content
            });
            if (!remappedPath) {
              return {
                success: false,
                persistedRemote: false,
                error: 'Unable to map message path after sync. Please refresh and try again.',
                path: persistedPath
              };
            }
            persistedPath = remappedPath;
            if (persistedPath !== normalizedPath) {
              const latestMessages = Array.isArray(this.state.messages) ? [...this.state.messages] : [];
              const latestIndex = latestMessages.findIndex((message) =>
                normalizeMessagePath(message?.path) === persistedPath
                && message?.role === existing.role
                && String(message?.characterId ?? '') === String(existing.characterId ?? '')
              );
              if (latestIndex >= 0) {
                latestMessages[latestIndex] = {
                  ...latestMessages[latestIndex],
                  content: nextContent,
                  path: persistedPath
                };
                this.state.messages = latestMessages;
                await this._saveMessagesToLocalStorage();
              }
            }
            remoteResult = await this._postMessageEdit(conversationId, persistedPath, nextContent);
          }
        }
        if (!remoteResult.ok) {
          console.warn('HangoutState.updateMessageContent: remote edit failed', remoteResult);
          return {
            success: false,
            persistedRemote: false,
            error: remoteResult.error || 'Remote edit failed',
            path: persistedPath
          };
        }
    }

    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED, {
      messagePath: persistedPath,
      content: nextContent
    });
    return { success: true, persistedRemote: shouldPersistRemote, path: persistedPath };
  }

  getCharacters() {
    return this.state.allCharacters;
  }

  getScenarios() {
    return this.state.allScenarios || [];
  }

  getRecentCharacters() {
    return this.state.recentCharacters;
  }

  getSelectedCharacters() {
    const config = this._getConfig();
    const activeCharacterIds = config.activeCharacterIds || this.getCurrentSession()?.activeCharacterIds || [];
    return this.state.allCharacters.filter(c => activeCharacterIds.includes(c.id));
  }

  getSelectedCharacter() {
    const config = this._getConfig();

    if (config.allCharacters?.[0]?.id) {
      const character = this.state.allCharacters.find(c => String(c.id) === String(config.allCharacters[0].id));
      if (character) return character;
    }

    const activeCharacters = this.getSelectedCharacters();
    if (activeCharacters.length > 0) return activeCharacters[0];

    const currentSession = this.getCurrentSession();
    if (currentSession?.characterId) {
      const sessionCharacter = this.state.allCharacters.find(c =>
        String(c.id) === String(currentSession.characterId)
      );
      if (sessionCharacter) return sessionCharacter;
    }

    return this.state.allCharacters[0] || null;
  }

  getSelectedScenario() {
    const config = this._getConfig();

    const scenario = config.selectedScenario;
    if (scenario) return scenario;

    const scenarioId = config.scenarioId;
    if (scenarioId && this.state.allScenarios?.length > 0) {
      const foundScenario = this.state.allScenarios.find(s => String(s.id) === String(scenarioId));
      if (foundScenario) {
        this.switchScenario(foundScenario);
        return foundScenario;
      }
    }

    return null;
  }

  async getSelectedSpot() {
    const config = this._getConfig();
    const currentLocationId = config.loc_id;
    if (currentLocationId && currentLocationId !== '0' && !String(currentLocationId).startsWith('ERROR')) {
      const selectedSpot = this.state.allLocations.find(l => String(l.id) === String(currentLocationId));
      if (selectedSpot) return selectedSpot;
      const locationName = await this._getLocationNameByBackgroundId(currentLocationId);
      return {
        id: currentLocationId,
        loc: currentLocationId,
        background: currentLocationId,
        name: locationName,
        locationName
      };
    }
    const sessionLocationId = this.getCurrentSession()?.loc_id;
    return this.state.allLocations.find(l => l.id === sessionLocationId) || null;
  }

  isLoading() {
    return this.state.isLoading;
  }

  getError() {
    return this.state.error;
  }

  subscribe(eventType, callback) {
    return this.events.on(eventType, callback);
  }

  unsubscribe(eventType, callback) {
    this.events.off(eventType, callback);
  }

  async clear() {
    this.state = {
      currentSession: null,
      messages: [],
      allCharacters: this.state.allCharacters,
      allScenarios: [],
      allLocations: [],
      conversationId: null,
      isLoading: false,
      error: null,
      currentTime: 'night',
      customScenarioDescription: null,
      customScenarioDescriptionCleared: false,
      customGoals: {},
      recentCharacters: [],
      activeVersions: {},
      pendingBranch: null,
      branchTipPath: null
    };

    await this.clearConversationStorageKeys();
    await this.storage.removeItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION);
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }
  _collectStorageKeysByPrefix(prefixes = []) {
    const keys = [];
    for (let i = 0; i < storageMechanism.length; i++) {
      const key = storageMechanism.key(i);
      if (!key) continue;
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        keys.push(key);
      }
    }
    return keys;
  }

  async clearConversationStorageKeys() {
    const keysToRemove = [
      STORAGE_KEYS.HISTORY,
      STORAGE_KEYS.OPENING_GREETING,
      ...this._collectStorageKeysByPrefix([
        STORAGE_PREFIXES.HANGOUT_TIME
      ])
    ];
    const uniqueKeys = [...new Set(keysToRemove)];
    if (uniqueKeys.length === 0) return [];
    await Promise.all(uniqueKeys.map((key) => this.storage.removeItem(key)));
    uniqueKeys.forEach((key) => storageMechanism.removeItem(key));
    storage.clearOpeningGreetingMessages();
    return uniqueKeys;
  }


  async updateCurrentGoal(newGoal, characterId = null) {
    const targetCharacterId = characterId || this.getCurrentSession()?.characterId;
    if (!targetCharacterId) {
      console.warn('HangoutState.updateCurrentGoal: No character ID available');
      return;
    }

    const previousGoal = this.state.customGoals?.[targetCharacterId];
    this.state.customGoals = this.state.customGoals || {};
    this.state.customGoals[targetCharacterId] = newGoal;

    if (previousGoal !== newGoal) {
      this.addMessage({
        role: 'system',
        content: `Character goal updated: ${newGoal ? newGoal.substring(0, 100) + (newGoal.length > 100 ? '...' : '') : 'cleared'}`,
        createdAt: Date.now(),
        path: `${Date.now()}`,
        metadata: {
          goalChange: true,
          characterId: targetCharacterId,
          previousGoal,
          newGoal
        }
      }, false);
    }

    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }

  getCurrentGoal(characterId = null) {
    const targetCharacterId = characterId || this.getCurrentSession()?.characterId;
    if (!targetCharacterId) return null;

    return this.state.customGoals?.[targetCharacterId] || null;
  }


  async setActiveCharacters(characters) {
    if (!Array.isArray(characters)) {
      console.warn('Characters must be an array');
      return;
    }

    this._updateConfig({ allCharacters: characters });
  }

  async setConversationId(conversationId) {
    const previousConversationId = this.getConversationId();
    const normalizedConversationId = typeof conversationId === 'string' ? conversationId.trim() : null;
    this.state.conversationId = normalizedConversationId;
    if (this.state.currentSession && typeof this.state.currentSession === 'object') {
      this.state.currentSession = {
        ...this.state.currentSession,
        conversationId: normalizedConversationId
      };
    }
    this._updateConfig({
      conversationId: normalizedConversationId,
      ...(normalizedConversationId ? { isNewConversation: false } : {})
    });
    const shouldHydratePendingMessages = !!normalizedConversationId
      && previousConversationId !== normalizedConversationId
      && Array.isArray(this.state.messages)
      && this.state.messages.length > 0;
    if (shouldHydratePendingMessages) {
      await this._saveMessagesToLocalStorage();
      await this._saveState();
    }
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }


  getConversationId() {
    const hangoutConfig = this._getConfig();
    const resolvedConversationId = hangoutConfig.conversationId
      || this.getCurrentSession()?.conversationId
      || this.state.conversationId
      || null;
    if (typeof resolvedConversationId !== 'string') return resolvedConversationId;
    const normalizedConversationId = resolvedConversationId.trim();
    return isPersistableConversationId(normalizedConversationId) ? normalizedConversationId : null;
  }

  async resumeConversation(conversationId, characterId, scenarioId) {
    if (!conversationId || !characterId) {
      this.setError(new Error('Invalid conversation parameters: conversationId and characterId are required'));
      return false;
    }

    let restorationResult = { success: false };
    try {
      const { conversationRestorationService } = await import('../../../core/services/ConversationRestorationService.js');
      await conversationRestorationService.initialize(this);
      restorationResult = await conversationRestorationService.restoreFromHistory(conversationId, {
        characterId,
        scenarioId,
        source: 'resume_conversation'
      });
    } catch (serviceError) {
      console.warn('ConversationRestorationService error, using fallback:', serviceError);
    }

    if (restorationResult.success) {
      this._emitChange(HANGOUT_EVENTS.CONVERSATION_RESUMED, {
        conversationId,
        characterId,
        scenarioId,
        restorationResult,
        source: 'ConversationRestorationService'
      });
      return true;
    }

    return await this._resumeConversationFallback(conversationId, characterId, scenarioId);
  }

  async _resumeConversationFallback(conversationId, characterId, scenarioId) {
    await this.setConversationId(conversationId);

    const conversationMetadata = this._getLocalConversationMetadata(conversationId);
    const localMessages = this._loadMessagesFromLocalStorage();

    if (localMessages?.length > 0) {
      this.setMessages(localMessages);
      this._loadActiveVersionsForConversation(conversationId);
      await this._reconcileIfLocalHasSiblingAssistantRounds(conversationId);
    }

    let messages = [];
    let lastMessage = null;
    let sessionStateFromDB = null;

    try {
      if (this.state.messages?.length > 0) {
        messages = this.state.messages;
        lastMessage = messages[messages.length - 1];
        sessionStateFromDB = await this._reconstructSessionStateFromMessages(messages);
      } else {
        messages = await this._fetchExistingMessages(conversationId);
        if (messages?.length > 0) {
          this.setMessages(messages);
          this._loadActiveVersionsForConversation(conversationId);
          lastMessage = messages[messages.length - 1];
          sessionStateFromDB = await this._reconstructSessionStateFromMessages(messages);
        }
      }
    } catch (messageError) {
      console.warn('Could not load existing messages:', messageError);
      if (this.state.messages.length > 0) {
        lastMessage = this.state.messages[this.state.messages.length - 1];
      }
    }

    const originalConfig = this._getConfig();
    const allCharactersData = this._extractCharacterData(sessionStateFromDB, originalConfig, conversationMetadata, characterId, lastMessage);

    const hangoutConfig = this._buildHangoutConfig(conversationMetadata, lastMessage, originalConfig, allCharactersData, characterId, scenarioId, conversationId);

    this._setConfig(hangoutConfig);

    this._updateCurrentSession(hangoutConfig, allCharactersData, conversationId, characterId, scenarioId);
    this._restoreSessionState(hangoutConfig, allCharactersData);

    await this._saveState();

    this._emitChange(HANGOUT_EVENTS.CONVERSATION_RESUMED, {
      conversationId,
      characterId,
      scenarioId,
      metadata: hangoutConfig,
      isMultiCharacter: allCharactersData.length > 1,
      characterCount: allCharactersData.length,
      characters: allCharactersData
    });

    return true;
  }


  _extractCharacterData(sessionStateFromDB, originalConfig, conversationMetadata, characterId, lastMessage) {
    const extracted = extractCharacterData({
      sessionStateFromDB,
      originalConfig,
      conversationMetadata,
      characterId,
      lastMessage,
      messages: this.state.messages
    });
    if (extracted.locId) {
      this._updateConfig({ loc_id: extracted.locId });
    }
    return extracted.characters;
  }

  _buildHangoutConfig(conversationMetadata, lastMessage, originalConfig, allCharactersData, characterId, scenarioId, conversationId) {
    const visualCharacterMap = buildCharacterVisualMap(originalConfig, conversationMetadata);
    const hangoutConfig = {
      scenarioId,
      conversationId,
      isNewConversation: false,
      loc_id: conversationMetadata?.backgroundId || lastMessage?.backgroundId || 0,
      timeOfDay: conversationMetadata?.timeOfDay || lastMessage?.timeOfDay || 'night',
      scenarioDescription: getScenarioDescriptionFromHangoutConfig({
        scenarioDescription: conversationMetadata?.scenarioDescription || originalConfig.scenarioDescription,
        customScenarioDescription: conversationMetadata?.customScenarioDescription
      }),
      customGoal: conversationMetadata?.customScenarioDescription || originalConfig.customGoal,
      scenarioType: conversationMetadata?.scenarioType || (conversationMetadata?.customScenarioDescription ? 'custom' : 'original'),
      allCharacters: allCharactersData.map((char) => mergeCharacterVisualData(char, visualCharacterMap)),
      backgroundUrl: null
    };

    if (hangoutConfig.loc_id && hangoutConfig.loc_id !== '0') {
      hangoutConfig.backgroundUrl = getBackgroundService().constructBackgroundUrl(
        hangoutConfig.loc_id,
        hangoutConfig.timeOfDay || 'night'
      );
    }

    return hangoutConfig;
  }


  _updateCurrentSession(hangoutConfig, allCharactersData, conversationId, characterId, scenarioId) {
    this._updateConfig({
      conversationId,
      characterId,
      scenarioId,
      loc_id: hangoutConfig.loc_id,
      backgroundUrl: hangoutConfig.backgroundUrl,
      isNewConversation: false,
      startTime: Date.now(),
      allCharacters: allCharactersData,
      activeCharacterIds: allCharactersData.map(c => String(c.id)),
      resumedAt: new Date().toISOString(),
      characterCount: allCharactersData.length,
      isMultiCharacter: allCharactersData.length > 1
    });
  }

  async _restoreSessionState(hangoutConfig, allCharactersData) {
    if ((hangoutConfig.customGoal || hangoutConfig.scenarioDescription) &&
      !this.state.customScenarioDescriptionCleared) {
      this.state.customScenarioDescription = hangoutConfig.customGoal || hangoutConfig.scenarioDescription;
    }

    if (allCharactersData.length > 0) {
      this.state.customGoals = this.state.customGoals || {};
      allCharactersData.forEach(char => {
        if (char.goal) {
          this.state.customGoals[char.id] = true;
        }
      });
    }

    if (hangoutConfig.backgroundUrl) {
      this.state.selectedBackground = hangoutConfig.backgroundUrl;
    }

    if (hangoutConfig.timeOfDay) {
      this.state.currentTime = hangoutConfig.timeOfDay;
      await this.setTimeSlot(hangoutConfig.timeOfDay, false);
    }
  }




  getCustomScenarioDescription() {
    return this.state.customScenarioDescription || storage.get(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION);
  }

  async setCustomScenarioDescription(description) {
    this.state.customScenarioDescription = description;
    this.state.customScenarioDescriptionCleared = false;

    const hangoutConfig = this._getConfig();
    hangoutConfig.customGoal = description;
    this._setConfig(hangoutConfig);

    await this.storage.setItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION, description);
    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }

  async clearCustomScenarioDescription() {
    this.state.customScenarioDescription = null;
    this.state.customScenarioDescriptionCleared = true;

    const hangoutConfig = this._getConfig();
    delete hangoutConfig.customGoal;
    this._setConfig(hangoutConfig);

    await this.storage.removeItem(STORAGE_KEYS.CUSTOM_SCENARIO_DESCRIPTION);
    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
  }

  getCurrentTimeSlot() {
    const config = this._getConfig();
    return config.timeOfDay || this.state.currentTime || ERROR_VALUES.MISSING_TIME;
  }

  async _getLocationNameByBackgroundId(backgroundId) {
    const fromState = this.state.allLocations?.find(loc =>
      String(loc.id) === String(backgroundId) ||
      String(loc.backgroundId) === String(backgroundId)
    );
    if (fromState?.name) return fromState.name;
    return await getLocationNameById(backgroundId, '???');
  }


  async addCharacter(character) {
    if (!character || !character.id) {
      console.warn('HangoutState.addCharacter: Invalid character');
      return;
    }

    const existingIndex = this.state.allCharacters.findIndex(c => c.id === character.id);

    if (existingIndex !== -1) {
      this.state.allCharacters[existingIndex].addedAtMessageId = `msg_${Date.now()}`;
      this.state.allCharacters[existingIndex].removedAtMessageId = null;
    } else {
      this.state.allCharacters.push({
        ...character,
        addedAtMessageId: `msg_${Date.now()}`,
        removedAtMessageId: null
      });
    }

    await this._saveState();
  }

  async switchScenario(scenario) {
    this.state.selectedScenario = scenario;
    const hangoutConfig = this._getConfig();
    hangoutConfig.selectedScenario = scenario;
    this._setConfig(hangoutConfig);
    await this._saveState();
  }

  async switchLocation(location) {
    if (location) {
      const config = this._getConfig();
      const timeOfDay = location.timeOfDay || config.timeOfDay || 'night';
      let backgroundUrl = location.backgroundUrl || null;
      if (!backgroundUrl && location.background) {
        backgroundUrl = typeof location.background === 'string' && location.background.includes('http')
          ? String(location.background)
          : getBackgroundService().constructBackgroundUrl(location.background, timeOfDay);
      }
      const locationUpdates = {};
      if (location.id) locationUpdates.loc_id = String(location.id);
      if (backgroundUrl) locationUpdates.backgroundUrl = String(backgroundUrl);
      if (location.timeOfDay) locationUpdates.timeOfDay = location.timeOfDay;
      this._updateConfig(locationUpdates);
      if (backgroundUrl) this.state.selectedBackground = backgroundUrl;
      if (location.timeOfDay) this.state.currentTime = location.timeOfDay;
    }

    await this._saveState();
    const conversationId = this.getConversationId();
    if (conversationId && location) {
      await this._saveMessagesToLocalStorage();
    }
    this._emitChange(HANGOUT_EVENTS.SPOT_SELECTED, location);
  }


  async getRecentConversations({ characterIds = [], limit = 20, cursor = null } = {}) {
    const requestedCharacterIds = [...new Set(
      (Array.isArray(characterIds) ? characterIds : [characterIds])
        .map((value) => (value === undefined || value === null || value === '' ? null : String(value)))
        .filter(Boolean)
    )];
    const characterId = requestedCharacterIds[0] || null;
    const resolvedLimit = Number.isFinite(Number(limit)) && Number(limit) > 0 ? Number(limit) : 20;
    const resolvedCursor = typeof cursor === 'string' && cursor.trim() ? cursor.trim() : null;

    const configCharacterNameMap = buildConfigCharacterNameMap(this._getConfig()?.allCharacters);
    const authToken = await tokenManager.getValidAccessToken();
    let allConversations = [];
    let nextCursor = null;

    if (authToken) {
      try {
        const query = new URLSearchParams({
          limit: String(resolvedLimit)
        });
        if (resolvedCursor) {
          query.set('cursor', resolvedCursor);
        }
        if (requestedCharacterIds.length > 0) {
          query.set('character_ids', requestedCharacterIds.join(','));
        }
        const conversationsApiUrl = `${GROK_WORKER_URL}/v1/hangout/conversations?${query.toString()}`;
        const data = await fetchCache.fetchJSON(conversationsApiUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          skipCache: true
        });
        if (Array.isArray(data?.conversations)) {
          allConversations = mapRemoteConversations(data.conversations, characterId, configCharacterNameMap);
        }
        nextCursor = typeof data?.nextCursor === 'string' && data.nextCursor ? data.nextCursor : null;
      } catch (error) {
        console.error('HangoutState.getRecentConversations: Error fetching from database:', error);
      }
    }

    if (resolvedCursor) {
      return {
        conversations: mergeAndSortConversations(allConversations),
        nextCursor
      };
    }

    const localConversations = [];
    for (const conversationId of this._getLocalConversationIds()) {
      if (allConversations.some((conv) => conv.conversationId === conversationId)) continue;
      const generalMetadata = this._getLocalConversationMetadata(conversationId);
      if (!generalMetadata) continue;
      const mapped = mapLocalConversation({
        generalMetadata,
        localMessages: storage.getConversationMessages(conversationId) || [],
        characterId: characterId || generalMetadata.characterId,
        uniqueRequestedCharacterIds: requestedCharacterIds,
        configCharacterNameMap
      });
      if (mapped) localConversations.push(mapped);
    }

    return {
      conversations: mergeAndSortConversations([...allConversations, ...localConversations]),
      nextCursor
    };
  }

  async setTimeSlot(timeSlot, isManualChange = true) {
    let normalizedTimeSlot = getTimeDefinitionService().resolveTimeAlias(timeSlot);
    this.state.currentTime = normalizedTimeSlot;

    const hangoutConfig = this._getConfig();
    hangoutConfig.timeOfDay = normalizedTimeSlot;
    this._setConfig(hangoutConfig);

    if (isManualChange) {
      storage.set('hangout_manual_time_set', 'true');
    }

    const currentBackground = await this.getSelectedBackground();
    if (currentBackground) {
      const backgroundId = extractBackgroundIdFromUrl(currentBackground);
      if (backgroundId && backgroundId !== '0') {
        await this.setSelectedBackground(backgroundId);
      }
    }

    await this._saveState();
    this._emitChange(HANGOUT_EVENTS.STATE_CHANGED);
    getBackgroundService().invalidateCache();
  }
}
